/**
 * designSystem — the Client Hub's graduated brand system (approved at
 * the P5 gate, ported intact from engine/clientHubDesigns per the
 * graduation principle; the sandbox re-exports from here). Locked brand
 * set: Carbon #16181B (D-004), Platinum #f2f2f2, Bright Indigo #0443ef for
 * structure/actions, Spicy Paprika #f76100 sparingly for at-risk/aging
 * only; Inter Tight + Inter; structured, minimal, geometric,
 * high-contrast, premium; no gradients.
 *
 * Big-screen is a hard requirement (operating-rules.md §11): containers
 * columnize (auto-fit grids), never stretch a single column. Motion is
 * tasteful and honors prefers-reduced-motion.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, useTheme, createTheme, ThemeProvider } from '@mui/material';
import { CalendarMonth as CalendarIcon, Close as ClearIcon } from '@mui/icons-material';
import { DateTimePicker } from '../../shared/DateTimePicker';
import { PopoverPortal, usePopoverPosition } from '../../shared/InlineEdit/popoverPortal';
import { PIcon } from '../../v2/icons';
import type { V2IconName } from '../../v2/icons';
import { installKeyboardKeeper } from '../../shared/windowKeyboard';

/**
 * DES-W1 (D-05, owner-ruled 2026-06-12): the HUB dialect re-points at the
 * mode-aware tokens so the Client Hub and Proposals follow day/sepia/night
 * like the rest of the app. Role mapping follows the approved pitch mock
 * (engine/contrastPitch — its Client Hub page proved this split):
 *   carbon → --ink        (strong ink: 2px rules, active fills, diamonds)
 *   platinum → --bg       (the page field)
 *   muted → --ink-ghost   (placeholders / disabled — sub-AA by design)
 * In day mode every value resolves to (visually) what the old literals
 * were — the re-point is invisible until the mode flips.
 *
 * The deliberately mode-invariant brand surfaces — Carbon heading bands
 * and client-facing share documents — do NOT ride this: they read BRAND
 * below (bands) or carry their own literals (share docs render outside
 * the app's theme entirely).
 */
export const HUB = {
  carbon: 'var(--ink)',
  platinum: 'var(--bg)',
  indigo: 'var(--indigo)',
  indigoInk: 'var(--indigo-ink)',
  indigoSoft: 'var(--indigo-soft)',
  paprika: 'var(--paprika)',
  paprikaSoft: 'var(--paprika-soft)',
  line: 'var(--rule)',
  muted: 'var(--ink-disabled)', // D-043: was --ink-ghost (same value)
  ink: 'var(--ink)',
  inkSoft: 'var(--ink-soft)',
  inkFaint: 'var(--ink-faint)',
  paper: 'var(--paper)',
} as const;

/**
 * BRAND — the D-05 carve-out. Carbon heading bands (client hub file
 * header, the proposals "Checked in" band, the wizard cart rail) render
 * identically in every mode: Carbon ground, platinum text, brand indigo
 * full-stop, brand paprika accents. These are the locked literals those
 * band regions read; everything else reads HUB and follows the mode.
 * Design System 2.0 (D-004): the one carbon is #16181B (#191919 retires).
 */
export const BRAND = {
  carbon: '#16181B',
  platinum: '#f2f2f2',
  indigo: '#0443ef',
  paprika: '#f76100',
} as const;

export const FONT_DISPLAY = '"Inter Tight", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
export const FONT_BODY = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';

/** Injected once per design page: keyframes + reduced-motion guard. */
export const MotionStyles: React.FC = () => (
  <style>{`
    @keyframes hubFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes hubFadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes hubGrowBar { from { transform: scaleX(0); } to { transform: scaleX(1); } }
    @media (prefers-reduced-motion: reduce) {
      .hub-anim, .hub-anim * { animation: none !important; transition: none !important; }
    }
  `}</style>
);

/** The kit's arrival. Design System 2.0 (D-011): the shared `ds-rise`
 *  (theme/motion.css) over 600ms on --ease-out, `base` seconds apart (60ms by
 *  default), capped at ten steps — past the tenth, everything arrives together.
 *  Replaces the 450ms rise and its 0.6s cap. */
export const fadeUp = (i = 0, base = 0.06): object => ({
  animation: 'ds-rise var(--dur-rise, 600ms) var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1)) both',
  animationDelay: `${Math.min(i, 10) * base}s`,
});

/** The kicker — the uppercase label above a title (Design System 2.0, D-007):
 *  Inter Tight 11 / 800 / .12em, indigo when it names where you are (the
 *  default), `--ink-faint` when it labels. One voice: step 5 wave 1a moved both
 *  the classic (10.5/600/.32em) and the V2 (9.5/700/.15em) kickers onto it.
 *  `voice="v2"` keeps the V2 layout (no wrap, the parent's line-height); `size`
 *  is kept for existing callers and ignored — nothing is smaller than the scale. */
export const Kicker: React.FC<{
  children: React.ReactNode;
  color?: string;
  voice?: 'v2';
  /** Ignored since 2.0 (the kicker is 11). */
  size?: number;
  /** Inline style, applied last (the design's `style` spread). */
  style?: React.CSSProperties;
}> = ({ children, color = HUB.indigo, voice, style }) => (
  <Typography component="div" style={style} sx={{
    fontFamily: 'var(--font-head)', fontSize: 11, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color,
    ...(voice === 'v2' ? { whiteSpace: 'nowrap', lineHeight: 'inherit' } : { lineHeight: 1.2 }),
  }}>
    {children}
  </Typography>
);

/**
 * StatusTag — the small uppercase tag beside a title on a card, a panel or a
 * record head (Design System 2.0, D-017 B12; the published StatusTag page).
 * Inside a dense table row use `HubTablePill` instead.
 *
 * Six tones, soft fills only in the app (the solid square tag lives only on
 * share pages and PDFs, which carry their own markup):
 *   good  — status-on-track soft · watch — paprika soft · bad — paprika soft
 *   with a 1.5px paprika frame · info — indigo soft · line — a rule-strong
 *   hairline, ink-soft words · dash — the same hairline dashed (not set up yet).
 * Inter Tight 11 / 800, .12em, uppercase, 3px corners, one line.
 *
 * Step 5 wave 1a moved every existing kind onto those tones (Claude's call
 * under D-016, D-046): done · on-track → good; paprika-soft · warning → watch;
 * off · off-track → bad; on · indigo · indigo-soft · pivot → info; pend ·
 * indicator · ghost → line; dormant → dash. `carbon` is the published tag for a
 * carbon surface (carbon-3 ground, on-carbon words). `build` keeps the build
 * family's own soft amber: a build block is a scheduling geometry, not a status.
 * `voice="v2"` draws the same tag (it used to be the 9.5 V2 tag).
 */
export type StatusTagKind =
  | 'good' | 'watch' | 'bad' | 'info' | 'line' | 'dash'
  | 'done' | 'on' | 'off' | 'pivot' | 'pend' | 'carbon' | 'indigo' | 'indigo-soft' | 'build'
  | 'on-track' | 'off-track' | 'warning' | 'indicator' | 'dormant' | 'paprika-soft' | 'ghost';
type TagTone = 'good' | 'watch' | 'bad' | 'info' | 'line' | 'dash' | 'carbon' | 'build';
const TAG_TONE: Record<StatusTagKind, TagTone> = {
  good: 'good', watch: 'watch', bad: 'bad', info: 'info', line: 'line', dash: 'dash',
  done: 'good', 'on-track': 'good',
  'paprika-soft': 'watch', warning: 'watch',
  off: 'bad', 'off-track': 'bad',
  on: 'info', indigo: 'info', 'indigo-soft': 'info', pivot: 'info',
  pend: 'line', indicator: 'line', ghost: 'line',
  dormant: 'dash', carbon: 'carbon', build: 'build',
};
const TAG_STYLE: Record<TagTone, React.CSSProperties> = {
  good: { background: 'var(--status-on-track-soft)', color: 'var(--status-on-track-on-soft)' },
  watch: { background: 'var(--paprika-soft)', color: 'var(--paprika-on-soft)' },
  bad: { background: 'var(--paprika-soft)', color: 'var(--paprika-on-soft)', boxShadow: 'inset 0 0 0 1.5px var(--paprika)' },
  info: { background: 'var(--indigo-soft)', color: 'var(--indigo-on-soft)' },
  line: { padding: '3px 7px', border: '1px solid var(--rule-strong)', color: 'var(--ink-soft)' },
  dash: { padding: '3px 7px', border: '1px dashed var(--rule-strong)', color: 'var(--ink-soft)' },
  carbon: { background: 'var(--carbon-3)', color: 'var(--on-carbon)' },
  build: { background: 'var(--build-soft)', color: 'var(--build-ink)' },
};

export const StatusTag: React.FC<{
  kind: StatusTagKind;
  children: React.ReactNode;
  /** Kept for existing callers; it draws the same 2.0 tag. */
  voice?: 'v2';
  /** Inline style, applied last (the design's `style` spread). */
  style?: React.CSSProperties;
}> = ({ kind, children, style }) => (
  <span
    data-tone={TAG_TONE[kind]}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 3,
      fontFamily: 'var(--font-head)', fontSize: 11, fontWeight: 800, letterSpacing: '.12em', lineHeight: 1.2,
      textTransform: 'uppercase', whiteSpace: 'nowrap', ...TAG_STYLE[TAG_TONE[kind]], ...style,
    }}
  >
    {children}
  </span>
);

/** Diamond bullet — the document's rotated-square list marker.
 *  `filled` paints the marker solid instead of outlining it: the two read as
 *  "set" vs "not set" in a list where both appear (the Build Block Scheduler's
 *  selection rows use a filled diamond for a picked block and the outline for
 *  an empty slot). Outline stays the default so every existing caller is
 *  byte-identical. */
export const Diamond: React.FC<{
  color?: string;
  size?: number;
  filled?: boolean;
  /** The marker's frame: `solid` (default, 1px), `dashed` (Portal V2's "not yet"
   *  diamond) or `none` (Portal V2's solid diamond is fill only). */
  outline?: 'solid' | 'dashed' | 'none';
  /** Frame width in px (default 1; Portal V2's dashed diamond is 1.5). */
  outlineWidth?: number;
  /** Inline style, applied last (the design's `style` spread). */
  style?: React.CSSProperties;
}> = ({ color = HUB.carbon, size = 5, filled, outline = 'solid', outlineWidth = 1, style }) => (
  <Box component="span" style={style} sx={{ width: size, height: size, border: outline === 'none' ? 'none' : `${outlineWidth}px ${outline} ${color}`, bgcolor: filled ? color : 'transparent', transform: 'rotate(45deg)', display: 'inline-block', flexShrink: 0 }} />
);

/**
 * HubThemeScope — the consistency layer (P7, Michael's feedback: "go
 * through every input and upgrade the visuals to match our designs …
 * not miss any modal, any dropdown, any select").
 *
 * One scoped MUI theme over the hub tree: every TextField, Select, Menu,
 * Dialog, Chip, and Button inside (including dialogs — theme context
 * flows through portals) gets the Carbon grammar — 4px squareness,
 * Inter Tight weight on buttons/titles, the 2px ink rule under dialog
 * titles, hairline borders, no elevation shadows, indigo focus. Palette
 * and mode come from the OUTER theme so the day/sepia/night sweep
 * inherits this for free.
 */
export const HubThemeScope: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const outer = useTheme();
  // Every themed page installs the one keyboard keeper (shared/windowKeyboard.ts):
  // when the window on top closes without handing focus on, the one under it gets it.
  useEffect(() => { installKeyboardKeeper(); }, []);
  // Design System 2.0 (step 5 wave 0): the base theme (src/theme.ts) now carries
  // the system's type scale, corners, the six shadows, the one menu, the carbon
  // tooltip, the 40-tall field with its indigo frame and halo, and chips on the
  // 3px corner — so every stock MUI control in the app moves at once, scoped or
  // not. This scope keeps only what the hub grammar adds on top: the button's
  // face, the dialog's hairline, rise and ruled title.
  const theme = useMemo(() => createTheme(outer, {
    shape: { borderRadius: 4 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none', fontFamily: FONT_DISPLAY, fontWeight: 700,
            borderRadius: 4, boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      },
      MuiOutlinedInput: { styleOverrides: { root: { fontFamily: FONT_BODY } } },
      MuiInputLabel: { styleOverrides: { root: { fontFamily: FONT_BODY } } },
      // DES-W1 addendum (a): the standard dialog enter/exit. Design System 2.0
      // (D-011, D-036): the paper rises on the shared `ds-dialog-rise`
      // (theme/motion.css — 10px from 98.5%) over the 250ms lift while MUI's
      // Fade brings the scrim in; exit is a 120ms fade. defaultProps can't read
      // CSS vars, so the durations are stated here. Reduced motion: the rise is
      // disabled; the opacity fade alone remains (the accessible fallback).
      MuiDialog: {
        defaultProps: {
          transitionDuration: { enter: 250, exit: 120 },
          // PQR-005: a window opened on top of a dialog keeps the caret (see theme.ts `dialogFocus`).
          disableEnforceFocus: true,
        },
        styleOverrides: {
          paper: {
            borderRadius: 6, border: `1px solid ${outer.palette.divider}`, backgroundImage: 'none',
            animation: 'ds-dialog-rise var(--dur-lift, 250ms) var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1))',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, letterSpacing: '-0.015em',
            borderBottom: `2px solid ${outer.palette.text.primary}`,
            paddingBottom: 10, marginBottom: 6,
          },
        },
      },
      MuiDialogActions: { styleOverrides: { root: { paddingLeft: 24, paddingRight: 24, paddingBottom: 16 } } },
      MuiTablePagination: { styleOverrides: { root: { fontFamily: FONT_BODY } } },
    },
  }), [outer]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

/** Page frame: platinum field, generous gutters that GROW on big screens.
 *  `inset` sits inside AppShell's content area (the live hub); the
 *  default fills the viewport (sandbox prototypes). Everything inside —
 *  dialogs included — renders under the HubThemeScope consistency layer. */
export const PageFrame: React.FC<{ children: React.ReactNode; inset?: boolean }> = ({ children, inset }) => (
  <HubThemeScope>
    <Box className="hub-anim" sx={{
      minHeight: inset ? undefined : '100vh',
      bgcolor: HUB.platinum, color: HUB.ink, fontFamily: FONT_BODY,
      px: inset ? { xs: 1.5, md: 2.5, lg: 4, xl: 6 } : { xs: 2.5, md: 4, lg: 6, xl: 8 },
      py: inset ? { xs: 1.5, md: 2 } : { xs: 2.5, md: 3.5 },
      borderRadius: inset ? '6px' : 0,
    }}>
      <MotionStyles />
      {children}
    </Box>
  </HubThemeScope>
);

/** The Carbon section heading: title + crumb over a 2px carbon rule. */
export const SheetHead: React.FC<{ title: string; crumb: string }> = ({ title, crumb }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: `2px solid ${HUB.carbon}`, pb: 1.25, mb: 2 }}>
    <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22, letterSpacing: '-0.01em' }}>{title}</Typography>
    <Typography sx={{ fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: HUB.inkFaint }}>{crumb}</Typography>
  </Box>
);

/**
 * HubDateField — THE date input (P7.1, Michael: "our calendar menu here
 * is old and not our new one"). Wraps the shared Beacon DateTimePicker
 * (preset chips + day/month/year drill) in the InlineEdit popover mount;
 * native <input type="date"> is banned from designed surfaces.
 *
 * Value in/out is a date-only string (YYYY-MM-DD). Clearable when
 * `onChange(null)` makes sense (e.g. next meeting date).
 */
export const HubDateField: React.FC<{
  label: string;
  value: string | null;
  onChange: (next: string | null) => void;
  helperText?: string;
  clearable?: boolean;
  disabled?: boolean;
  minWidth?: number | string;
  /** Style for placement on a mode-invariant Carbon band (ProposalWizard's
   *  checkout rail, #16181B ground, colorScheme dark): FIXED light-ink hues
   *  so the trigger reads on the dark ground in EVERY mode. Without it the
   *  trigger rides the mode-aware --ink, which is dark-on-dark in day mode on
   *  a Carbon band (the HubButton onCarbon crush, applied to the date field).
   *  The popover (Beacon DateTimePicker) is unaffected; value in/out contract
   *  (YYYY-MM-DD) is unchanged. */
  onCarbon?: boolean;
  /** The phone (MOBILE-2E P5 review): the picker opens IN THE FLOW under the
   *  trigger — stacked to the width of its container, a day only — instead
   *  of the 460px popover, which runs off a 390px screen. For a phone sheet,
   *  whose body scrolls; the value in/out contract is unchanged. */
  inline?: boolean;
  /** With `inline`: a day is applied the moment it is tapped and the picker
   *  closes — no Save of its own (HubDatePop's grammar). For a phone sheet
   *  that has its own Save, so the two never sit side by side and a tapped
   *  day is never left staged (MOBILE-2E TODO+ review round 1). */
  applyOnPick?: boolean;
}> = ({ label, value, onChange, helperText, clearable, disabled, minWidth = 180, onCarbon, inline = false, applyOnPick = false }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  // The popover's width (the Beacon picker's 460px) clamps it inside the window.
  const coords = usePopoverPosition(anchorRef as any, open && !inline, 380, 460);
  const toggle = () => setOpen((o) => (inline ? !o : true));
  // Inline, the picker opens under the trigger in a scrolling sheet: bring it into view.
  const inlineRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = inlineRef.current;
    if (inline && open && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [inline, open]);

  const display = value
    ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  // Carbon-band fixed literals (mirrors HubButton onCarbon / the toast carbon
  // band): light ink that never rides the mode. #6E93FF is the carbon-indigo
  // focus hue — brighter than brand indigo so it reads on the dark ground.
  // BRAND.platinum reused for value/icon light so no new solid hex is added.
  const CARBON_LABEL = 'rgba(244,245,247,0.6)';
  const CARBON_BORDER = 'rgba(255,255,255,0.22)';
  const CARBON_FILL = 'rgba(255,255,255,0.06)';
  const CARBON_INDIGO = '#6E93FF';

  return (
    <Box sx={{ minWidth }}>
      <Typography sx={{ fontFamily: FONT_DISPLAY, fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: onCarbon ? CARBON_LABEL : HUB.inkFaint, fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        ref={anchorRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-expanded={inline ? open : undefined}
        onClick={() => { if (!disabled) toggle(); }}
        onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(); } }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          border: '1px solid',
          borderColor: open ? (onCarbon ? CARBON_INDIGO : HUB.indigo) : (onCarbon ? CARBON_BORDER : HUB.line),
          borderWidth: open ? 1.5 : 1,
          borderRadius: '4px', px: 1.25, py: 0.9,
          cursor: disabled ? 'default' : 'pointer', userSelect: 'none',
          bgcolor: disabled ? (onCarbon ? CARBON_FILL : 'action.hover') : (onCarbon ? CARBON_FILL : 'background.paper'),
          transition: 'border-color 0.15s ease',
          '&:hover': disabled ? {} : { borderColor: onCarbon ? CARBON_INDIGO : HUB.indigo },
        }}
      >
        <CalendarIcon sx={{ fontSize: 16, color: onCarbon ? (value ? CARBON_INDIGO : CARBON_LABEL) : (value ? HUB.indigo : HUB.muted) }} />
        <Typography sx={{ fontSize: 13.5, fontFamily: FONT_BODY, color: onCarbon ? (value ? BRAND.platinum : CARBON_LABEL) : (value ? 'text.primary' : HUB.muted), flex: 1, fontWeight: value ? 600 : 400 }}>
          {display || 'Pick a date…'}
        </Typography>
        {clearable && value && !disabled && (
          <IconButton
            aria-label={`Clear ${label}`}
            size="small"
            onClick={e => { e.stopPropagation(); onChange(null); }}
            sx={{ p: 0.25, mr: -0.5, color: onCarbon ? CARBON_LABEL : undefined }}
          >
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      {helperText && (
        <Typography sx={{ fontSize: 10.5, color: onCarbon ? CARBON_LABEL : HUB.inkFaint, mt: 0.5 }}>{helperText}</Typography>
      )}
      {inline ? (
        open ? (
          <Box ref={inlineRef} sx={{ mt: 1 }} data-part="date-picker">
            <DateTimePicker
              value={value ? `${value.slice(0, 10)}T12:00:00` : null}
              onSave={iso => { onChange(localDayOf(iso)); setOpen(false); }}
              onCancel={() => setOpen(false)}
              stacked
              dateOnly
              applyOnPick={applyOnPick}
            />
          </Box>
        ) : null
      ) : (
        <PopoverPortal open={open} coords={coords} onClickOutside={() => setOpen(false)} animate>
          <DateTimePicker
            value={value ? `${value.slice(0, 10)}T12:00:00` : null}
            onSave={iso => { onChange(localDayOf(iso)); setOpen(false); }}
            onCancel={() => setOpen(false)}
          />
        </PopoverPortal>
      )}
    </Box>
  );
};

/** Date-only consumers: keep the local calendar day the picker shows. */
function localDayOf(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * HubButton — the one button (Design System 2.0, D-017 B1; the published
 * HubButton page). Both of its voices render the same 2.0 look, so every
 * caller moved with step 5 wave 1a without touching its own code:
 *   kinds   primary (indigo, `--on-indigo` label, `--indigo-ink` on hover)
 *           · secondary (1px ink outline; fills ink with a paper label on hover)
 *           · quiet (no frame, indigo text, indigo-soft on hover — a lone skip)
 *           · danger (paprika outline, `--paprika-ink` words, D-048; fills paprika on hover)
 *           · oncarbon (`--rule-on-carbon` frame, `--on-carbon` label; carbon-3
 *             with an `--indigo-on-carbon` frame on hover)
 *   sizes   sm 32 (12.5) in rows, toolbars and the bulk bar · md 40 (13.5),
 *           the default · lg 48 (14/800), the one call to action, with the
 *           indigo lift and a 2px rise on hover.
 * Inter Tight 700, 4px corners, no shadow at rest, 120ms state changes on
 * --ease-out, a press to 97.5%, the global indigo focus ring, `.42` when
 * disabled. Icons take the label's colour.
 *
 * The V2 voice keeps its props (`kind`, `small`, `lg`, `icon`, `iconRight`,
 * `block`): `outline` is the secondary, `ghost` the quiet; `carbon` (an ink
 * fill) is not a 2.0 kind and stays as it was drawn for the screens that use it.
 * The classic voice (`variant` primary · ghost · danger, `onCarbon`) maps onto
 * the same kinds and defaults to the 32 size, its old footprint (it was ~29
 * tall); pass `size` for 40 or 48.
 */
export type HubButtonV2Kind = 'primary' | 'secondary' | 'outline' | 'quiet' | 'ghost' | 'danger' | 'oncarbon' | 'carbon';
export type HubButtonSize = 'sm' | 'md' | 'lg';
export type HubButtonV2Props = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  voice: 'v2';
  /** Default `outline` (the secondary). */
  kind?: HubButtonV2Kind;
  /** The 32 size (12.5). */
  small?: boolean;
  /** The 48 call to action (14/800, the indigo lift on hover). */
  lg?: boolean;
  /** The size by name; wins over `small` / `lg`. */
  size?: HubButtonSize;
  /** An icon from the one line set before / after the label (14 at 32, else 16). */
  icon?: V2IconName;
  iconRight?: V2IconName;
  /** Full width, `display: flex`. */
  block?: boolean;
  children?: React.ReactNode;
};
type HubButtonHubProps = {
  voice?: undefined;
  variant?: 'primary' | 'ghost' | 'danger';
  onClick?: () => void;
  disabled?: boolean;
  /** For a mode-invariant carbon band: the ghost becomes the on-carbon kind
   *  (a primary or danger keeps its own kind — both read on carbon). */
  onCarbon?: boolean;
  /** Default `sm` (32), the classic voice's footprint. */
  size?: HubButtonSize;
  /** Focus this button on mount (e.g. Cancel in a confirm). */
  autoFocus?: boolean;
  children: React.ReactNode;
};

type DsKind = 'primary' | 'secondary' | 'quiet' | 'danger' | 'oncarbon' | 'carbon';
const DS_KIND_ALIAS: Record<HubButtonV2Kind, DsKind> = {
  primary: 'primary', secondary: 'secondary', outline: 'secondary', quiet: 'quiet', ghost: 'quiet',
  danger: 'danger', oncarbon: 'oncarbon', carbon: 'carbon',
};
/* Rest colours go inline (they are the button's identity); hover rides the sx
   with !important so it can beat them, the pattern HUB_ITEM_SX uses. */
const DS_KIND_REST: Record<DsKind, React.CSSProperties> = {
  primary: { background: 'var(--indigo)', color: 'var(--on-indigo)', border: '1px solid var(--indigo)' },
  secondary: { background: 'transparent', color: 'var(--ink)', border: '1px solid var(--ink)' },
  quiet: { background: 'transparent', color: 'var(--indigo)', border: '1px solid transparent' },
  danger: { background: 'transparent', color: 'var(--paprika-ink)', border: '1px solid var(--paprika)' },
  oncarbon: { background: 'transparent', color: 'var(--on-carbon)', border: '1px solid var(--rule-on-carbon)' },
  carbon: { background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)' },
};
const DS_KIND_HOVER: Record<DsKind, Record<string, string>> = {
  primary: { background: 'var(--indigo-ink) !important', borderColor: 'var(--indigo-ink) !important' },
  secondary: { background: 'var(--ink) !important', color: 'var(--paper) !important' },
  quiet: { background: 'var(--indigo-soft) !important' },
  danger: { background: 'var(--paprika) !important', color: 'var(--on-paprika) !important' },
  oncarbon: { background: 'var(--carbon-3) !important', borderColor: 'var(--indigo-on-carbon) !important' },
  carbon: { background: 'var(--ink-soft) !important', borderColor: 'var(--ink-soft) !important' },
};
const DS_SIZE: Record<HubButtonSize, React.CSSProperties> = {
  sm: { minHeight: 32, padding: '0 10px', fontSize: 12.5, fontWeight: 700 },
  md: { minHeight: 40, padding: '0 14px', fontSize: 13.5, fontWeight: 700 },
  lg: { minHeight: 48, padding: '0 20px', fontSize: 14, fontWeight: 800 },
};
const DS_ICON: Record<HubButtonSize, number> = { sm: 14, md: 16, lg: 16 };

/** The sx both voices share: state changes, the press, the kind's hover and the
 *  call to action's lift — none of them while disabled (either kind of disabled). */
function dsButtonSx(kind: DsKind, size: HubButtonSize) {
  return {
    transition: 'background var(--dur-state, 120ms) var(--ease-out), color var(--dur-state, 120ms) var(--ease-out), border-color var(--dur-state, 120ms) var(--ease-out), transform var(--dur-state, 120ms) var(--ease-out), box-shadow var(--dur-lift, 250ms) var(--ease-out)',
    '& svg': { flex: 'none' },
    '&:hover:not(:disabled):not([aria-disabled="true"])': {
      ...DS_KIND_HOVER[kind],
      ...(size === 'lg' ? { boxShadow: 'var(--lift-indigo)', transform: 'translateY(-2px)' } : {}),
    },
    '&:active:not(:disabled):not([aria-disabled="true"])': { transform: 'scale(.975)' },
  } as const;
}
function dsButtonStyle(kind: DsKind, size: HubButtonSize, disabled: boolean | undefined): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    borderRadius: 4, fontFamily: 'var(--font-head)', lineHeight: 1, whiteSpace: 'nowrap', userSelect: 'none',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.42 : 1,
    ...DS_SIZE[size], ...(kind === 'quiet' ? { padding: '0 8px' } : null), ...DS_KIND_REST[kind],
  };
}

const HubButtonV2: React.FC<HubButtonV2Props> = ({ voice: _voice, kind = 'outline', small, lg, size: sizeProp, icon, iconRight, children, disabled, block, style, ...rest }) => {
  const size: HubButtonSize = sizeProp ?? (small ? 'sm' : lg ? 'lg' : 'md');
  const k = DS_KIND_ALIAS[kind];
  return (
    <Box component="button" type="button" disabled={disabled} sx={dsButtonSx(k, size)} style={{ ...dsButtonStyle(k, size, disabled), display: block ? 'flex' : 'inline-flex', width: block ? '100%' : undefined, ...style }} {...rest}>
      {icon && <PIcon name={icon} size={DS_ICON[size]} color="currentColor" />}{children}{iconRight && <PIcon name={iconRight} size={DS_ICON[size]} color="currentColor" />}
    </Box>
  );
};

/** The one button: `voice="v2"` takes the V2 props (a real `<button>`); without
 *  it, the classic props (a focusable `role="button"`). Both draw the 2.0 look. */
export const HubButton: React.FC<HubButtonHubProps | HubButtonV2Props> = (props) => {
  if (props.voice === 'v2') return <HubButtonV2 {...props} />;
  return <HubButtonHub {...props} />;
};

/** The size a region gives its buttons when they don't name one — a modal's
 *  footer provides `md` (its buttons are 40 tall, the published HubModal page). */
export const HubButtonSizeContext = React.createContext<HubButtonSize | null>(null);

const HubButtonHub: React.FC<HubButtonHubProps> = ({ variant = 'ghost', onClick, disabled, onCarbon, size: sizeProp, autoFocus, children }) => {
  const regionSize = React.useContext(HubButtonSizeContext);
  const size: HubButtonSize = sizeProp ?? regionSize ?? 'sm';
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (autoFocus && !disabled) ref.current?.focus(); }, [autoFocus, disabled]);
  const kind: DsKind = variant === 'primary' ? 'primary' : variant === 'danger' ? 'danger' : onCarbon ? 'oncarbon' : 'secondary';
  return (
    <Box
      ref={ref}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick?.(); } }}
      sx={dsButtonSx(kind, size)}
      style={dsButtonStyle(kind, size, disabled)}
    >
      {children}
    </Box>
  );
};
